import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { scheduledActivityApi } from '../services/adminApi';
import { queryKeys } from '../utils/queryKeys';
import {
  CreateScheduledActivityDto,
  UpdateScheduledActivityDto,
  ScheduledActivitiesQueryParams,
} from '../types/scheduledActivity';

/**
 * Hook to fetch scheduled activities with optional filters
 */
export function useScheduledActivities(params?: ScheduledActivitiesQueryParams) {
  return useQuery({
    queryKey: queryKeys.scheduledActivities.list(params),
    queryFn: () => scheduledActivityApi.listScheduledActivities(params),
  });
}

/**
 * Hook to fetch scheduled activities for a specific date
 */
export function useScheduledActivitiesByDate(date: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.scheduledActivities.byDate(date),
    queryFn: () => scheduledActivityApi.getScheduledActivitiesForDate(date),
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a single scheduled activity
 */
export function useScheduledActivity(id: string) {
  return useQuery({
    queryKey: queryKeys.scheduledActivities.detail(id),
    queryFn: () => scheduledActivityApi.getScheduledActivity(id),
    enabled: !!id,
  });
}

/**
 * Hook to create a scheduled activity
 */
export function useCreateScheduledActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateScheduledActivityDto) =>
      scheduledActivityApi.createScheduledActivity(dto),
    onSuccess: () => {
      // Invalidate all scheduled activities queries
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduledActivities.all });
    },
  });
}

/**
 * Hook to update a scheduled activity
 */
export function useUpdateScheduledActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateScheduledActivityDto }) =>
      scheduledActivityApi.updateScheduledActivity(id, dto),
    onSuccess: () => {
      // Invalidate all scheduled activities queries
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduledActivities.all });
    },
  });
}

/**
 * Hook to delete a scheduled activity
 */
export function useDeleteScheduledActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => scheduledActivityApi.deleteScheduledActivity(id),
    onSuccess: () => {
      // Invalidate all scheduled activities queries
      queryClient.invalidateQueries({ queryKey: queryKeys.scheduledActivities.all });
    },
  });
}
